import {
  BanIcon,
  CalendarIcon,
  GaugeIcon,
  HourglassIcon,
  ShieldCheckIcon,
  SparklesIcon,
} from 'lucide-react';
import type { QuotaStatusDTO } from '@shared/schemas/quota';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import {
  formatCompactTokens,
  formatQuotaDate,
  formatTokenCount,
  getQuotaProgressPercent,
  getQuotaProgressTone,
} from '@/features/quota/quota-format';

function isExpirationPassed(iso: string) {
  return new Date(iso).getTime() <= Date.now();
}

function StatusKpi({
  icon: Icon,
  label,
  value,
  hint,
}: {
  hint?: string;
  icon: typeof GaugeIcon;
  label: string;
  value: string;
}) {
  return (
    <Card className="rounded-[var(--radius-surface-3xl)] border-border/70">
      <CardContent className="flex flex-col gap-3 p-5">
        <div className="flex items-center gap-2 text-muted-foreground">
          <span className="flex size-8 items-center justify-center rounded-full bg-muted text-foreground">
            <Icon className="size-4" />
          </span>
          <span className="text-sm font-medium">{label}</span>
        </div>
        <p className="text-2xl font-semibold tracking-tight text-foreground">{value}</p>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}

export function AdminUserQuotaStatusCard({
  demoExpiresAt,
  status,
}: {
  demoExpiresAt: string | null;
  status: QuotaStatusDTO;
}) {
  const isSubscriber = status.accountType === 'subscriber';
  const tokenLimit = status.tokenLimit ?? 0;
  const extraTokens = status.extraTokens ?? 0;
  const allowance = tokenLimit + extraTokens;
  const consumed = status.tokensConsumed ?? 0;
  const ratio = status.usageRatio ?? (allowance > 0 ? consumed / allowance : 0);
  const tone = getQuotaProgressTone(ratio, status.blocked);

  if (isSubscriber) {
    return (
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <StatusKpi
          icon={SparklesIcon}
          label="Type de compte"
          value="Abonné"
          hint="Aucune limite de consommation"
        />
        <StatusKpi
          icon={GaugeIcon}
          label="Tokens consommés"
          value={formatTokenCount(consumed)}
          hint="Consommation cumulée sur la période courante"
        />
        <StatusKpi
          icon={ShieldCheckIcon}
          label="Blocage"
          value="Non applicable"
          hint="Les comptes abonnés ne sont jamais bloqués"
        />
      </section>
    );
  }

  // Compte démo sans quota configuré : le modèle courant est temporel,
  // l'accès est piloté par la date d'expiration.
  if (status.tokenLimit === null) {
    const isExpired = demoExpiresAt !== null && isExpirationPassed(demoExpiresAt);
    return (
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <StatusKpi
          icon={SparklesIcon}
          label="Type de compte"
          value="Démo"
          hint="Accès limité dans le temps"
        />
        <StatusKpi
          icon={HourglassIcon}
          label="Expiration"
          value={demoExpiresAt === null ? 'Sans expiration' : formatQuotaDate(demoExpiresAt)}
          hint={isExpired
            ? 'Compte expiré : l’accès est bloqué jusqu’à réactivation'
            : 'Le compte sera désactivé automatiquement à cette date'}
        />
        <StatusKpi
          icon={GaugeIcon}
          label="Tokens consommés"
          value={formatTokenCount(consumed)}
          hint="Consommation cumulée sur la période courante"
        />
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatusKpi
          icon={GaugeIcon}
          label="Consommation"
          value={`${formatCompactTokens(consumed)} / ${formatCompactTokens(allowance)}`}
          hint={extraTokens > 0 ? `Dont ${formatCompactTokens(extraTokens)} extra` : undefined}
        />
        <StatusKpi
          icon={CalendarIcon}
          label="Renouvellement"
          value={formatQuotaDate(status.renewsAt)}
          hint={status.renewalIntervalDays ? `Tous les ${status.renewalIntervalDays} jours` : undefined}
        />
        <StatusKpi
          icon={ShieldCheckIcon}
          label="Enforcement"
          value={status.enforced ? 'Actif' : 'Désactivé'}
          hint={status.enforced ? 'Le chat est bloqué à la limite' : 'Aucun blocage appliqué'}
        />
        <StatusKpi
          icon={BanIcon}
          label="État"
          value={status.blocked ? 'Bloqué' : 'Actif'}
          hint={status.blocked ? 'Quota atteint pour cette période' : 'Consommation autorisée'}
        />
      </div>

      <Card className="rounded-[var(--radius-surface-3xl)] border-border/70">
        <CardContent className="space-y-3 p-5">
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="font-medium text-foreground">Progression de la période</span>
            <span className="text-muted-foreground">{Math.round((ratio ?? 0) * 100)} %</span>
          </div>
          <Progress
            value={getQuotaProgressPercent(consumed, allowance)}
            className={cn(
              'h-2',
              tone === 'success' && '[&_[data-slot=progress-indicator]]:bg-emerald-500',
              tone === 'warning' && '[&_[data-slot=progress-indicator]]:bg-orange-500',
              tone === 'danger' && '[&_[data-slot=progress-indicator]]:bg-destructive',
            )}
          />
          <p className="text-xs text-muted-foreground">
            Restant : {formatTokenCount(status.tokensRemaining ?? Math.max(0, allowance - consumed))} tokens
          </p>
        </CardContent>
      </Card>
    </section>
  );
}
